import { Button, Pill, Text } from '@innovationdepartment/proxima-ui';
import { BoltOutlined, ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import {
  Card,
  ExpandIcon,
  LastRunLabel,
  List,
  PayloadActions,
  PayloadError,
  PayloadHeader,
  PayloadLabel,
  PayloadPanel,
  PayloadTextarea,
  TriggerRowMain,
  WorkflowDescription
} from './BrandWorkflows.styles';
import type { KickOffError, LatestExecution } from './BrandWorkflows.types';
import { getManualTriggerEntries } from './workflowCatalog';
import { executionPillVariant } from './workflowFormatters';

const DEFAULT_PAYLOAD_TEXT = '{}';

/* The payload must be a plain JSON object — the API rejects arrays and primitives */
export const payloadParseError = (text: string): string | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return 'Payload is not valid JSON';
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Payload must be a JSON object';
  }
  return null;
};

type BrandWorkflowsManualTriggersListProps = {
  rows: LatestExecution[];
  expandedWorkflowType: string | null;
  kickingOffWorkflowType: string | null;
  kickOffError: KickOffError | null;
  onToggleExpand: (workflowType: string) => void;
  onKickOff: (workflowType: string, payloadText: string) => void;
};

const BrandWorkflowsManualTriggersList = ({
  rows,
  expandedWorkflowType,
  kickingOffWorkflowType,
  kickOffError,
  onToggleExpand,
  onKickOff
}: BrandWorkflowsManualTriggersListProps) => {
  const [payloadText, setPayloadText] = useState(DEFAULT_PAYLOAD_TEXT);

  // Only one row is expanded at a time; start each one from a clean payload
  // biome-ignore lint/correctness/useExhaustiveDependencies: expandedWorkflowType is the reset trigger and is intentionally not read inside the effect
  useEffect(() => {
    setPayloadText(DEFAULT_PAYLOAD_TEXT);
  }, [expandedWorkflowType]);

  const parseError = payloadParseError(payloadText);

  return (
    <List>
      {getManualTriggerEntries(rows).map(entry => {
        const expanded = expandedWorkflowType === entry.workflowType;
        const kickingOff = kickingOffWorkflowType === entry.workflowType;
        const serverError =
          kickOffError?.workflowType === entry.workflowType ? kickOffError.message : null;

        const onRowClick = (event: MouseEvent<HTMLDivElement>) => {
          if ((event.target as HTMLElement).closest('[data-no-row-toggle]')) return;
          onToggleExpand(entry.workflowType);
        };

        return (
          <Card key={entry.workflowType} failed={entry.lastRun.status === 'Failure'}>
            <TriggerRowMain
              role="button"
              tabIndex={0}
              aria-expanded={expanded}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${entry.displayName} trigger`}
              onClick={onRowClick}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleExpand(entry.workflowType);
                }
              }}
            >
              <div>
                <Text variant="body1Semibold">{entry.displayName}</Text>
                {entry.description ? (
                  <WorkflowDescription>
                    <Text variant="body2" color="neutral60">
                      {entry.description}
                    </Text>
                  </WorkflowDescription>
                ) : null}
              </div>
              <div>
                <LastRunLabel>Last run</LastRunLabel>
                <Pill
                  label={entry.lastRun.status}
                  variant={executionPillVariant(entry.lastRun.status, entry.lastRun.isFirstRun)}
                />
              </div>
              <ExpandIcon>
                {expanded ? <ExpandMoreOutlined /> : <ChevronRightOutlined />}
              </ExpandIcon>
            </TriggerRowMain>
            {expanded && (
              <PayloadPanel>
                <PayloadHeader>
                  <PayloadLabel>{entry.schemaName ?? 'No Trigger Payload'}</PayloadLabel>
                </PayloadHeader>
                <PayloadTextarea
                  aria-label={`${entry.displayName} trigger payload`}
                  value={payloadText}
                  onChange={event => setPayloadText(event.target.value)}
                  spellCheck={false}
                  disabled={!entry.schemaName}
                />
                {parseError || serverError ? (
                  <PayloadError>{parseError ?? serverError}</PayloadError>
                ) : null}
                <PayloadActions>
                  <Button
                    label="Cancel"
                    variant="text"
                    size="small"
                    onClick={() => onToggleExpand(entry.workflowType)}
                  />
                  <Button
                    label="Kick off"
                    size="small"
                    startIcon={<BoltOutlined />}
                    disabled={Boolean(parseError) || kickingOff}
                    onClick={() => onKickOff(entry.workflowType, payloadText)}
                  />
                </PayloadActions>
              </PayloadPanel>
            )}
          </Card>
        );
      })}
    </List>
  );
};

export default BrandWorkflowsManualTriggersList;
